import { Note, NoteSet } from '../types/note';
import { Chord } from '../types/chord';
import { hashNotes } from './noteUtils';

function canOmitRoot(chord: Chord): boolean {
  return chord.countUniqueNotes() > 3;
}

function canOmitFifth(chord: Chord): boolean {
  if (!chord.fifth || chord.countUniqueNotes() < 4) {
    return false;
  }
  return (chord.root.value + 7) % 12 === chord.fifth.value;
}

function canOmitNinth(chord: Chord): boolean {
  if (!chord.ninth || chord.countUniqueNotes() < 4) {
    return false;
  }
  if (!chord.thirteenth) {
    return false;
  }
  return (chord.root.value + 2) % 12 === chord.ninth.value;
}

function canOmitEleventh(chord: Chord): boolean {
  if (!chord.eleventh || chord.countUniqueNotes() < 4) {
    return false;
  }
  if (!chord.thirteenth) {
    return false;
  }
  return (chord.root.value + 5) % 12 === chord.eleventh.value;
}

function addHashWithOmittedNote(result: number[], note: Note): void {
  const newHashes: number[] = [];
  for (const hash of result) {
    newHashes.push(hash & ~(1 << note.value));
  }
  result.push(...newHashes);
}

const _chordHashCache: WeakMap<Chord, number[]> = new WeakMap();
function generateChordHashes(chord: Chord): number[] {
  if (_chordHashCache.has(chord)) {
    return _chordHashCache.get(chord)!;
  }
  let key = 0;
  if (chord.root) key |= 1 << chord.root.value;
  if (chord.third) key |= 1 << chord.third.value;
  if (chord.fifth) key |= 1 << chord.fifth.value;
  if (chord.seventh) key |= 1 << chord.seventh.value;
  if (chord.ninth) key |= 1 << chord.ninth.value;
  if (chord.eleventh) key |= 1 << chord.eleventh.value;
  if (chord.thirteenth) key |= 1 << chord.thirteenth.value;
  if (chord.altRoot) key |= 1 << chord.altRoot.value;

  const result: number[] = [key];
  if (canOmitRoot(chord)) {
    addHashWithOmittedNote(result, chord.root);
  }
  if (canOmitFifth(chord) && chord.fifth) {
    addHashWithOmittedNote(result, chord.fifth);
  }
  if (canOmitNinth(chord) && chord.ninth) {
    addHashWithOmittedNote(result, chord.ninth);
  }
  if (canOmitEleventh(chord) && chord.eleventh) {
    addHashWithOmittedNote(result, chord.eleventh);
  }
  Object.freeze(result);
  _chordHashCache.set(chord, result);
  return result;
}

export function checkChordMatch(chord: Chord, notes: NoteSet): boolean {
  if (notes.size < 2) {
    return false;
  }
  if (chord.altRoot && !notes.bottomNote?.norm().equals(chord.altRoot)) {
    return false;
  }
  const chordHashes = generateChordHashes(chord);
  const notesHash = hashNotes(notes);
  return chordHashes.includes(notesHash);
}

export function getHint(chord: Chord, keyboardRange: { start: number; end: number }): Note[] {
  let middle_f = Math.floor((keyboardRange.start + keyboardRange.end) / 2 / 12) * 12 + 5;

  // Get the notes from the chord
  function invert(notes: NoteSet) {
    const bottom = notes.bottomNote;
    notes.delete(bottom!);
    notes.add(bottom!.transpose(12));
  }
  function transpose(notes: NoteSet, offset: number) {
    const res: Note[] = notes.map((note: Note) => note);
    res.forEach((note: Note) => {
      notes.delete(note);
      notes.add(note.transpose(offset));
    });
  }
  let notes = new NoteSet(chord.notes);
  if (chord.altRoot) {
    while (notes.bottomNote?.value !== chord.altRoot.value) {
      invert(notes);
    }
  } else {
    while (notes.bottomNote?.value !== chord.root.value) {
      invert(notes);
    }
  }
  if (notes.topNote) {
    while (notes.topNote?.value <= middle_f) {
      transpose(notes, 12);
    }
    while (notes.topNote?.value > keyboardRange.end) {
      transpose(notes, -12);
    }
  }
  return notes.map((note: Note) => note);
}
